import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Switch } from 'src/design/atoms/Switch';
import { TextInput } from 'src/design/atoms/TextInput';
import React from 'react';
import {
  useDeletePhone,
  useOptOutOfSms,
  useRequestPhoneVerification,
  useUpdateNotificationChannels,
  useVerifyPhone,
} from 'src/api/queries';
import { useAuth } from 'src/hooks/useAuth';
import { InlineConfirmButton } from 'src/design/molecules/InlineConfirmButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { Header } from 'src/design/organisms/Header';
import { PhoneVerificationStepper } from 'src/app/notifications/PhoneVerificationStepper';
import { Modal } from 'src/design/molecules/Modal';
import { useConfirmableAction } from 'src/design/molecules/useConfirmableAction';
import { DangerButton } from 'src/design/atoms/DangerButton';
import { FireMemorySection } from 'src/routes/AccountProfile/FireMemorySection';

const formatPhoneForDisplay = (phoneE164?: string): string => {
  if (!phoneE164) return '';
  const digits = phoneE164.replace(/\D/g, '').slice(-10);
  if (digits.length !== 10) return phoneE164;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const AccountProfile: React.FC = () => {
  const { user, loadUserFromCache, checkFeatureAllowed } = useAuth();
  const [phoneModalOpen, setPhoneModalOpen] = React.useState(false);

  const updateChannelsMutation = useUpdateNotificationChannels();
  const optOutMutation = useOptOutOfSms();
  const deletePhoneMutation = useDeletePhone();
  const requestCodeMutation = useRequestPhoneVerification();
  const verifyMutation = useVerifyPhone();

  const channels = user?.notificationChannels ?? [];
  const hasVerifiedPhone = Boolean(user?.phoneNumberVerifiedAt);
  const isSmsConsented = user?.smsConsentStatus === 'consented';
  const smsSwitchDisabled = !hasVerifiedPhone || !isSmsConsented;

  const toggleChannel = async (channel: string, enabled: boolean) => {
    const next = enabled
      ? [...new Set([...channels, channel])]
      : channels.filter((c) => c !== channel);
    await updateChannelsMutation.mutateAsync(next);
    await loadUserFromCache();
  };

  const emailOff = useConfirmableAction(
    () => toggleChannel('email', false),
    true,
  );

  const smsOff = useConfirmableAction(async () => {
    await optOutMutation.mutateAsync();
    await loadUserFromCache();
  }, true);

  const handleEmailToggle = (enabled: boolean) => {
    if (enabled) {
      toggleChannel('email', true);
    } else {
      emailOff.requestOrRun();
    }
  };

  const handleSmsToggle = (enabled: boolean) => {
    // Only "off" is reachable through the switch; turning SMS back on
    // requires re-verifying the phone. There's no bare re-enable route —
    // PhoneVerificationStepper's verify step sets consent back to Consented
    // as a side effect.
    if (!enabled) {
      smsOff.requestOrRun();
    }
  };

  const handleDeletePhone = async () => {
    await deletePhoneMutation.mutateAsync();
    await loadUserFromCache();
  };

  const handleVerified = async () => {
    await loadUserFromCache();
    setPhoneModalOpen(false);
  };

  return (
    <>
      <Header
        title="Profile"
        breadcrumbProps={{
          items: [{ text: 'Account' }, { text: 'Profile' }],
        }}
        helpText={'View your account profile.'}
      />
      <Paper p="md" mt="md">
        <Stack maw="400px">
          <TextInput
            label="First Name"
            disabled
            value={user?.firstName || ''}
          />
          <TextInput label="Last Name" disabled value={user?.lastName || ''} />
          <TextInput label="Email" disabled value={user?.email || ''} />
        </Stack>
      </Paper>

      <Paper p="md" mt="md">
        <Stack maw="400px">
          <Text fw={600}>Phone Number</Text>
          {user?.phoneNumber ? (
            <>
              <Group justify="space-between">
                <Text>{formatPhoneForDisplay(user.phoneNumber)}</Text>
                {hasVerifiedPhone && (
                  <Badge color="green" variant="light">
                    Verified
                  </Badge>
                )}
              </Group>
              <Group>
                <SecondaryButton
                  size="sm"
                  onClick={() => setPhoneModalOpen(true)}
                >
                  Change
                </SecondaryButton>
                <InlineConfirmButton
                  size="sm"
                  color="red"
                  onConfirmClick={handleDeletePhone}
                  loading={deletePhoneMutation.isPending}
                >
                  Remove
                </InlineConfirmButton>
              </Group>
            </>
          ) : (
            <Group>
              <Text c="dimmed" size="sm">
                No phone number on file.
              </Text>
              <SecondaryButton
                size="sm"
                onClick={() => setPhoneModalOpen(true)}
              >
                Add Phone Number
              </SecondaryButton>
            </Group>
          )}
        </Stack>
      </Paper>

      <Paper p="md" mt="md">
        <Stack maw="400px">
          <Text fw={600}>Notifications</Text>
          <Switch
            label="Email"
            checked={channels.includes('email')}
            onChange={(e) => handleEmailToggle(e.currentTarget.checked)}
          />
          <Switch
            label="Text Message"
            description={
              smsSwitchDisabled
                ? hasVerifiedPhone
                  ? 'Re-verify your phone number with Change to re-enable text messages.'
                  : 'Verify a phone number to enable'
                : undefined
            }
            checked={isSmsConsented}
            disabled={smsSwitchDisabled}
            onChange={(e) => handleSmsToggle(e.currentTarget.checked)}
          />
        </Stack>
      </Paper>

      {checkFeatureAllowed(['chatbot']) && <FireMemorySection />}

      <Modal
        size="md"
        title="Turn off email notifications?"
        opened={emailOff.opened}
        onClose={emailOff.close}
        actions={[
          <DangerButton
            key="confirm"
            loading={emailOff.loading}
            onClick={() => emailOff.confirm()}
          >
            Turn off email
          </DangerButton>,
        ]}
      >
        <Text>
          You will stop getting email notifications, such as timesheet reviews
          and workflow updates. You can turn them back on here at any time.
        </Text>
      </Modal>

      <Modal
        size="md"
        title="Turn off text messages?"
        opened={smsOff.opened}
        onClose={smsOff.close}
        actions={[
          <DangerButton
            key="confirm"
            loading={smsOff.loading}
            onClick={() => smsOff.confirm()}
          >
            Turn off texts
          </DangerButton>,
        ]}
      >
        <Text>
          You will stop getting text messages. To turn them back on, you must
          verify your phone number again.
        </Text>
      </Modal>

      <Modal
        title={user?.phoneNumber ? 'Change Phone Number' : 'Add Phone Number'}
        opened={phoneModalOpen}
        onClose={() => setPhoneModalOpen(false)}
      >
        <PhoneVerificationStepper
          onRequestCode={async (phone) => {
            await requestCodeMutation.mutateAsync(phone);
          }}
          onVerifyCode={async (code) => {
            await verifyMutation.mutateAsync(code);
          }}
          onVerified={handleVerified}
        />
      </Modal>
    </>
  );
};
